import { inject, makeStateKey, REQUEST, StateKey, TransferState, Service } from '@angular/core';
import { PlatformService } from '@core/platform/platform.service';
import { StorageService } from '@core/storage/storage.service';
import { StorageKeys } from '@core/storage/storage.keys';
import { Language, toLanguage } from '@core/i18n/domain/language';
import { parseLangParam, readLangQueryParam } from './lang-param';

const LANG_STATE_KEY: StateKey<Language> = makeStateKey<Language>('v2.language');

/** Same name in both storage systems — no collision, they're separate stores. */
const LANGUAGE_COOKIE_NAME = StorageKeys.LANGUAGE;

/**
 * LanguageService — the ONLY place that persists/transfers the language.
 *
 * Uses the SAME storage key as the TranslationFacade (`StorageKeys.LANGUAGE`)
 * so the selected language and the loaded translations always agree. Does NOT
 * touch the global <html> element (SeoService owns <html lang> per-page; the v2
 * shell host owns layout direction) — avoiding any clash with v1. SSR-safe.
 *
 * ── Server-side resolution ──────────────────────────────────────────────────
 * `load()` used to return `null` on the server unconditionally, so EVERY server
 * render — including a crawler's and a link unfurl's — was Arabic, whatever the
 * url or the visitor asked for. With the whole application now server-rendered
 * (`app.routes.server.ts`), that meant `<html lang>`, `<meta name="description">`,
 * the OpenGraph card and the `hreflang` set were identical for `?lang=en` and
 * `?lang=ar`: the alternates claimed two languages the server only ever served
 * one of.
 *
 * It now resolves the same two explicit signals the BROWSER already honours, in
 * the same order, so the two platforms cannot disagree about the same request:
 *
 *   1. `?lang=` on the request url — the link author's explicit instruction. This
 *      is what the browser resolves first too: `LegacyHandoffService`
 *      .applyEntryLangParam() writes an entry `?lang=` into `StorageKeys.LANGUAGE`
 *      in the root component's constructor, BEFORE this service is ever read.
 *   2. the `lang` cookie — the returning visitor's own stored choice, mirrored
 *      from localStorage by {@link save} below (exactly the pattern
 *      `ThemePersistenceService` already uses, and for the same reason: the
 *      server cannot read localStorage, but it can read a cookie).
 *
 * `Accept-Language` is deliberately NOT consulted. It is neither of those two
 * signals — it is a browser default the visitor never set for this product — and
 * honouring it would silently switch a first-time visitor with an English browser
 * off the product's Arabic default, a behaviour change no part of this app asks
 * for. The two signals above are both explicit, so server and client always agree
 * and hydration stays clean.
 */
@Service()
export class LanguageService {
  private readonly platform = inject(PlatformService);
  private readonly storage = inject(StorageService);
  private readonly transferState = inject(TransferState);
  private readonly request = inject(REQUEST, { optional: true });

  load(): Language | null {
    if (this.platform.isBrowser) {
      const stored = this.storage.get<string>(StorageKeys.LANGUAGE);
      if (stored) return toLanguage(stored);
      if (this.transferState.hasKey(LANG_STATE_KEY)) {
        const t = this.transferState.get<Language>(LANG_STATE_KEY, Language.Ar);
        this.transferState.remove(LANG_STATE_KEY);
        return toLanguage(t);
      }
      return null;
    }

    // Server: the request url's `?lang=`, then the visitor's own cookie. A
    // first-time visitor has neither — the caller then uses the default, exactly
    // as before this resolution existed.
    const fromUrl = readLangQueryParam(this.requestSearch());
    if (fromUrl) return fromUrl;
    return parseLangParam(readCookie(this.request?.headers.get('cookie'), LANGUAGE_COOKIE_NAME));
  }

  save(lang: Language): void {
    if (this.platform.isBrowser) {
      this.storage.set(StorageKeys.LANGUAGE, lang);
      // Mirror the choice into a cookie so the NEXT request's server render
      // already knows it. localStorage stays the authority in the browser; this
      // is only what makes the server's first paint agree with it.
      setLanguageCookie(lang);
    } else {
      // Seeds the language the server actually rendered in, so a client with no
      // stored choice of its own hydrates with the same value (no mismatch).
      this.transferState.set(LANG_STATE_KEY, lang);
    }
  }

  /** The `?…` query string of the request being rendered (`''` when there is none). */
  private requestSearch(): string {
    const url = this.request?.url;
    if (!url) return '';
    const q = url.indexOf('?');
    return q === -1 ? '' : url.slice(q);
  }
}

// ── cookie helpers (browser-write / server-read) ────────────────────────────
// Same pattern as theme-persistence.service.ts — a plain parse/set, no
// dependency needed for one named value.

function setLanguageCookie(lang: Language): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year — matches localStorage's effectively-indefinite lifetime
  document.cookie = `${LANGUAGE_COOKIE_NAME}=${encodeURIComponent(lang)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

function readCookie(cookieHeader: string | null | undefined, name: string): string | null {
  if (!cookieHeader) return null;
  const match = cookieHeader.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]!) : null;
}
